import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Mail, Phone, Save, FileText, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/admin-api';
import { formatDateTime, statusBadgeClass } from './formatters';

const STATUS_OPTIONS = ['new', 'reviewed', 'interview', 'hired', 'rejected', 'archived'];

function yn(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

function Field({ label, value }) {
  return (
    <div>
      <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-1">{label}</p>
      <p className="text-sm text-foreground whitespace-pre-wrap">{value || <span className="text-muted-foreground italic">—</span>}</p>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <Card className="editorial-card p-0">
      <CardContent className="p-6">
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-4">{title}</p>
        {children}
      </CardContent>
    </Card>
  );
}

function EmployerView({ employer, index }) {
  if (!employer || !employer.name) return null;
  return (
    <div className="rounded-lg border border-bone-2 p-4">
      <p className="font-display font-semibold text-foreground mb-3">
        {index === 0 ? 'Most Recent Employer' : `Employer ${index + 1}`}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name of Employer" value={employer.name} />
        <Field label="Address" value={employer.address} />
        <Field label="Telephone" value={employer.telephone} />
        <Field label="Date Started" value={employer.date_started} />
        <Field label="Starting Title" value={employer.starting_title} />
        <Field label="Starting Salary" value={employer.starting_salary} />
        <Field label="Ending Title" value={employer.ending_title} />
        <Field label="Date Left" value={employer.date_left} />
        <Field label="Ending Salary" value={employer.ending_salary} />
        <Field label="Last Supervisor" value={employer.last_supervisor} />
      </div>
      <div className="mt-4 space-y-3">
        <Field label="Reason for Leaving" value={employer.reason_for_leaving} />
        <Field label="Job Duties & Responsibilities" value={employer.job_duties} />
      </div>
    </div>
  );
}

function ReferenceView({ reference, index }) {
  if (!reference || !reference.name) return null;
  return (
    <div className="rounded-lg border border-bone-2 p-4">
      <p className="font-display font-semibold text-foreground mb-3">Reference {index + 1}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name" value={reference.name} />
        <Field label="Personal / Business" value={reference.ref_type} />
        <Field label="Address" value={reference.address} />
        <Field label="Telephone" value={reference.telephone} />
        <Field label="How Known" value={reference.how_known} />
      </div>
    </div>
  );
}

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusDraft, setStatusDraft] = useState('new');
  const [noteDraft, setNoteDraft] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await adminApi.get(`/applications/${id}`);
        if (!cancelled) {
          setData(response.data);
          setStatusDraft(response.data.status || 'new');
        }
      } catch (error) {
        if (!cancelled) {
          toast.error('Failed to load application');
          navigate('/admin/applications');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { status: statusDraft };
      if (noteDraft.trim()) payload.note = noteDraft.trim();
      const response = await adminApi.patch(`/applications/${id}`, payload);
      setData(response.data);
      setNoteDraft('');
      toast.success(`Application marked as “${statusDraft}”`);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Update failed';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleResumeDownload = async () => {
    try {
      const res = await adminApi.get(`/applications/${id}/resume`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.resume_filename || 'resume';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error('Could not download resume');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Application">
        <Skeleton className="h-64 w-full" />
      </AdminLayout>
    );
  }
  if (!data) return null;

  const dirty = statusDraft !== (data.status || 'new') || noteDraft.trim().length > 0;
  const notes = Array.isArray(data.notes) ? data.notes : [];
  const employers = Array.isArray(data.employers) ? data.employers : [];
  const references = Array.isArray(data.references) ? data.references : [];
  const fullAddress = [data.address_street, data.address_line2, [data.address_city, data.address_state].filter(Boolean).join(', '), data.address_zip, data.address_country]
    .filter(Boolean)
    .join('\n');

  return (
    <AdminLayout>
      <Link
        to="/admin/applications"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-5"
        data-testid="admin-application-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to applications
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground" data-testid="admin-application-name">
            {data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim()}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.position_applied_for || data.role_applying_for} · Submitted {formatDateTime(data.submitted_at)}
          </p>
        </div>
        <Badge
          variant="secondary"
          className={`font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 ${statusBadgeClass(data.status)}`}
          data-testid="admin-application-current-status"
        >
          {data.status || 'new'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main panel */}
        <div className="lg:col-span-2 space-y-5">
          <SectionCard title="Position & Personal Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Position Applied For" value={data.position_applied_for || data.role_applying_for} />
              <Field label="Full Name" value={`${data.first_name || ''} ${data.last_name || ''}`.trim()} />
              <Field label="Email" value={data.email ? (
                <a href={`mailto:${data.email}`} className="hover:text-primary inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {data.email}
                </a>
              ) : null} />
              <Field label="Home Telephone" value={data.home_telephone ? (
                <a href={`tel:${data.home_telephone}`} className="hover:text-primary inline-flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  {data.home_telephone}
                </a>
              ) : null} />
              <Field label="Work Telephone" value={data.work_telephone} />
              <div className="sm:col-span-2">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-1">Current Address</p>
                <p className="text-sm text-foreground whitespace-pre-wrap inline-flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{fullAddress || <span className="text-muted-foreground italic">—</span>}</span>
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Eligibility">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Availability to Work" value={(data.availability || []).join(', ')} />
              <Field label="At least 18 years old" value={yn(data.at_least_18)} />
              <Field label="Immigration / Visa Prohibited" value={yn(data.immigration_prohibited)} />
              <Field label="Convicted of a Felony" value={yn(data.convicted_felony)} />
            </div>
            {data.convicted_felony ? (
              <div className="mt-4">
                <Field label="Felony Explanation" value={data.felony_explanation} />
              </div>
            ) : null}
          </SectionCard>

          <SectionCard title="Employment History">
            <div className="space-y-4">
              {employers.some((e) => e && e.name)
                ? employers.map((employer, index) => <EmployerView key={`employer-row-${index}`} employer={employer} index={index} />)
                : <p className="text-sm text-muted-foreground italic">No employment history provided.</p>}
            </div>
          </SectionCard>

          <SectionCard title="Skills, Certifications & Aptitudes">
            <Field label="Skills / Certifications / Aptitudes" value={data.skills} />
          </SectionCard>

          <SectionCard title="Education">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="High School Attended" value={data.high_school} />
                <Field label="Graduate?" value={yn(data.high_school_graduate)} />
                <Field label="Course of Study" value={data.high_school_course} />
              </div>
              {(data.college1 || data.college2 || data.grad_school) ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-3 border-t border-bone-2">
                  {data.college1 ? <Field label="College (Undergrad)" value={`${data.college1} — ${yn(data.college1_graduate)} · ${data.college1_course || ''} ${data.college1_degree || ''}`.trim()} /> : null}
                  {data.college2 ? <Field label="College (Undergrad)" value={`${data.college2} — ${yn(data.college2_graduate)} · ${data.college2_course || ''} ${data.college2_degree || ''}`.trim()} /> : null}
                  {data.grad_school ? <Field label="Graduate / Professional" value={`${data.grad_school} — ${yn(data.grad_school_graduate)} · ${data.grad_school_course || ''} ${data.grad_school_degree || ''}`.trim()} /> : null}
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-3 border-t border-bone-2">
                <Field label="Additional Education / Training" value={data.additional_education} />
                <Field label="Other Interests" value={data.other_interests} />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="References">
            <div className="space-y-4">
              {references.some((r) => r && r.name)
                ? references.map((reference, index) => <ReferenceView key={`reference-row-${index}`} reference={reference} index={index} />)
                : <p className="text-sm text-muted-foreground italic">No references provided.</p>}
            </div>
          </SectionCard>

          <SectionCard title="Certification">
            <Field label="Certified By (printed name)" value={data.certification_name} />
          </SectionCard>
        </div>

        {/* Sidebar: resume + status + notes */}
        <div className="space-y-5">
          <Card className="editorial-card p-0">
            <CardContent className="p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">Resume</p>
              {data.resume_path ? (
                <Button variant="outline" className="w-full gap-2" onClick={handleResumeDownload} data-testid="admin-application-resume-download">
                  <FileText className="w-3.5 h-3.5" />
                  Download {data.resume_filename || 'resume'}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground italic">No resume on file.</p>
              )}
            </CardContent>
          </Card>

          <Card className="editorial-card p-0" data-testid="admin-application-status-panel">
            <CardContent className="p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">Status &amp; Note</p>
              <Select value={statusDraft} onValueChange={setStatusDraft}>
                <SelectTrigger data-testid="admin-application-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                className="mt-3 min-h-[100px] resize-none"
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                placeholder="Optional note (e.g., 'Called for phone screen — left voicemail')"
                data-testid="admin-application-note"
              />
              <Button
                className="w-full mt-3 gap-1.5"
                disabled={!dirty || saving}
                onClick={handleSave}
                data-testid="admin-application-save"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>

          <Card className="editorial-card p-0">
            <CardContent className="p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">History</p>
              {notes.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No notes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {[...notes].reverse().map((note, index) => (
                    <li key={note.at || index} className="text-xs">
                      <p className="text-muted-foreground font-mono">
                        {formatDateTime(note.at)} — {note.by}
                      </p>
                      <p className="text-foreground/80 mt-0.5 whitespace-pre-wrap">{note.text}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
